import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { RealtimeContext, type Handler, type RealtimeEvent } from './realtime-store.js';
import { apiOrigin, getAccessToken, refreshAccessToken } from '../lib/api-client';
import { useAuth } from '../auth/auth-store';

/**
 * One socket for the whole tab (context.md §40). Phase 61.
 *
 * Every screen that wants live updates shares this connection and says which topics it cares about.
 * The alternative — a socket per screen, which is where this product started — costs a connection,
 * a Redis subscription and a heartbeat timer per open thread, per tab, per person. At a thousand
 * tenants that is the difference between tens of thousands of connections and hundreds of
 * thousands, for exactly the same information.
 *
 * Three things worth reading:
 *
 * **Reconnect with backoff and jitter.** A deploy drops every socket at once; if they all come back
 * on the same schedule they arrive as one wave and knock the new replica over. Random jitter turns
 * the wave into a spread.
 *
 * **A socket is not a delivery guarantee.** Anything that happened while it was down was missed, so
 * every subscriber is told when the connection comes back and refetches. This is the part people
 * skip, and it is why "it works until the wifi blinks" is the usual state of a real-time feature.
 *
 * **Polling does not go away, it slows down.** The screens keep a long refetch interval as a floor,
 * because a socket that is silently broken looks exactly like a quiet afternoon.
 */





/** Backoff between attempts, capped: a server that is down stays down for a while. */
const BACKOFF_MS = [500, 1_000, 2_000, 5_000, 10_000, 20_000];

export function RealtimeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const socket = useRef<WebSocket | null>(null);
  const handlers = useRef(new Map<string, Set<Handler>>());
  const reconnectHandlers = useRef(new Set<() => void>());
  const attempt = useRef(0);
  const timer = useRef<number | undefined>(undefined);
  const closing = useRef(false);

  const [connected, setConnected] = useState(false);
  const [online, setOnline] = useState<ReadonlySet<string>>(new Set());

  /*
   * The session, not the token.
   *
   * The access token lives in memory and arrives asynchronously — a reload recovers the session by
   * refreshing, so at the moment this provider first mounts there is usually no token at all. An
   * effect that read the token once would find nothing and never try again, which is a socket that
   * silently never connects. Keying the connection on the session status is what makes it open when
   * the session is actually there, and reopen after a re-login.
   */
  const { status } = useAuth();

  /** Tells the server every topic this tab currently wants. Used on open and on resubscribe. */
  const sendSubscriptions = useCallback(() => {
    const live = socket.current;

    if (live?.readyState !== WebSocket.OPEN) return;

    const topics = [...handlers.current.keys()];

    if (topics.length > 0) live.send(JSON.stringify({ type: 'subscribe', topics }));
  }, []);

  const connect = useCallback(() => {
    const token = getAccessToken();

    if (!token || closing.current) return;

    const url = new URL('/v1/realtime', apiOrigin());

    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.searchParams.set('token', token);

    const live = new WebSocket(url);
    socket.current = live;

    live.onopen = () => {
      attempt.current = 0;
      setConnected(true);
      sendSubscriptions();
    };

    live.onmessage = (event: MessageEvent<string>) => {
      let frame: RealtimeEvent & { online?: string[] };

      try {
        frame = JSON.parse(event.data) as RealtimeEvent & { online?: string[] };
      } catch {
        // A frame this build does not understand is not a reason to drop the connection.
        return;
      }

      if (frame.type === 'ready' && Array.isArray(frame.online)) {
        setOnline(new Set(frame.online));
        return;
      }

      if (frame.type === 'presence.changed') {
        const { userId, online: isOnline } = (frame.payload ?? {}) as {
          userId?: string;
          online?: boolean;
        };

        if (userId) {
          setOnline((current) => {
            const next = new Set(current);

            if (isOnline) next.add(userId);
            else next.delete(userId);

            return next;
          });
        }
      }

      for (const handler of handlers.current.get(frame.topic) ?? []) handler(frame);
    };

    live.onclose = (event: CloseEvent) => {
      setConnected(false);
      socket.current = null;

      if (closing.current) return;

      /*
       * 4401 is the gateway saying the token was expired at the upgrade.
       *
       * Reconnecting with the same token would fail exactly the same way, for ever: the socket
       * verifies the token once, at connect, so an access token that expires while a tab is open
       * only bites when the connection drops — a deploy, a sleeping laptop, a blink of wifi. The
       * renewal happens here, before the next attempt.
       */
      if (event.code === 4401) void refreshAccessToken().catch(() => undefined);

      /*
       * Backoff with jitter.
       *
       * A deploy drops every socket at once. Without the random part they all come back on the same
       * schedule and arrive as one wave; the jitter spreads them over the window instead.
       */
      const base = BACKOFF_MS[Math.min(attempt.current, BACKOFF_MS.length - 1)]!;
      const wait = base / 2 + Math.random() * base;

      attempt.current += 1;
      timer.current = window.setTimeout(() => {
        connect();

        // Everything that happened while this was down was missed; the screens reconcile.
        for (const notify of reconnectHandlers.current) notify();
      }, wait);
    };

    live.onerror = () => live.close();
  }, [sendSubscriptions]);

  useEffect(() => {
    if (status !== 'authenticated') return;

    closing.current = false;
    attempt.current = 0;
    connect();

    return () => {
      closing.current = true;
      window.clearTimeout(timer.current);
      socket.current?.close();
      socket.current = null;
      setConnected(false);
    };
  }, [connect, status]);

  const subscribe = useCallback((topic: string, handler: Handler) => {
    const existing = handlers.current.get(topic);

    if (existing) {
      existing.add(handler);
    } else {
      handlers.current.set(topic, new Set([handler]));

      const live = socket.current;

      if (live?.readyState === WebSocket.OPEN) {
        live.send(JSON.stringify({ type: 'subscribe', topics: [topic] }));
      }
    }

    return () => {
      const listeners = handlers.current.get(topic);

      if (!listeners) return;

      listeners.delete(handler);

      if (listeners.size > 0) return;

      handlers.current.delete(topic);

      const live = socket.current;

      if (live?.readyState === WebSocket.OPEN) {
        live.send(JSON.stringify({ type: 'unsubscribe', topics: [topic] }));
      }
    };
  }, []);

  const onReconnect = useCallback((handler: () => void) => {
    reconnectHandlers.current.add(handler);

    return () => {
      reconnectHandlers.current.delete(handler);
    };
  }, []);

  const value = useMemo(
    () => ({ connected, online, subscribe, onReconnect }),
    [connected, online, subscribe, onReconnect],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}



